import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus, Trash2, Save, Route, User, Calendar, Clock, MapPin } from 'lucide-react';
import ClienteSelector from '../components/ClienteSelector';
import LocalAvulsoCampos, { avulsoVazio, avulsoPreenchido, cadastrarLocalAvulso } from '../components/LocalAvulsoCampos';
import { todayLocalYMD } from '../lib/utils';

const fieldClass =
  'w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-shadow';

const hojeISO = () => todayLocalYMD();

const visitaVazia = (ordem = 1) => ({
  modo: 'cliente',
  cliente_id: '',
  cliente_codigo: '',
  cliente_nome: '',
  avulso: avulsoVazio(),
  ordem,
  horario_agendado: '',
  tipo_servico: '',
});

const visitaPronta = (visita) => {
  if (!visita.tipo_servico) return false;
  if (visita.modo === 'avulso') return avulsoPreenchido(visita.avulso);
  return Boolean(visita.cliente_id);
};

const diaVazio = (data = hojeISO()) => ({
  data,
  visitas: [],
});

const NovaRota = () => {
  const navigate = useNavigate();
  const [tecnicos, setTecnicos] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tecnicoId, setTecnicoId] = useState('');
  const [dias, setDias] = useState([diaVazio()]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, tiposRes] = await Promise.all([
        api.get('/users', { params: { funcao: 'tecnico' } }),
        api.get('/tipos-servico-rota', { params: { ativo: true } }),
      ]);
      setTecnicos(usersRes.data?.users || []);
      setTiposServico(Array.isArray(tiposRes.data) ? tiposRes.data : []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  const addDia = () => {
    const ultimaData = dias[dias.length - 1]?.data || hojeISO();
    let proxima = hojeISO();
    const m = String(ultimaData).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      d.setDate(d.getDate() + 1);
      const y = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      proxima = `${y}-${mo}-${day}`;
    }
    setDias([...dias, diaVazio(proxima)]);
  };

  const removeDia = (diaIndex) => {
    if (dias.length <= 1) return;
    setDias(dias.filter((_, i) => i !== diaIndex));
  };

  const updateDiaData = (diaIndex, data) => {
    const next = [...dias];
    next[diaIndex] = { ...next[diaIndex], data };
    setDias(next);
  };

  const addVisita = (diaIndex) => {
    const next = [...dias];
    const visitas = next[diaIndex].visitas;
    next[diaIndex] = {
      ...next[diaIndex],
      visitas: [...visitas, visitaVazia(visitas.length + 1)],
    };
    setDias(next);
  };

  const removeVisita = (diaIndex, visitaIndex) => {
    const next = [...dias];
    next[diaIndex] = {
      ...next[diaIndex],
      visitas: next[diaIndex].visitas
        .filter((_, i) => i !== visitaIndex)
        .map((v, i) => ({ ...v, ordem: i + 1 })),
    };
    setDias(next);
  };

  const updateVisita = (diaIndex, visitaIndex, field, value) => {
    const next = [...dias];
    const visitas = [...next[diaIndex].visitas];
    visitas[visitaIndex] = { ...visitas[visitaIndex], [field]: value };
    next[diaIndex] = { ...next[diaIndex], visitas };
    setDias(next);
  };

  const updateVisitaCliente = (diaIndex, visitaIndex, cliente) => {
    const next = [...dias];
    const visitas = [...next[diaIndex].visitas];
    if (cliente) {
      visitas[visitaIndex] = {
        ...visitas[visitaIndex],
        cliente_id: cliente.id,
        cliente_codigo: cliente.codigo,
        cliente_nome: cliente.nome,
      };
    } else {
      visitas[visitaIndex] = {
        ...visitas[visitaIndex],
        cliente_id: '',
        cliente_codigo: '',
        cliente_nome: '',
      };
    }
    next[diaIndex] = { ...next[diaIndex], visitas };
    setDias(next);
  };

  const setVisitaModo = (diaIndex, visitaIndex, modo) => {
    const next = [...dias];
    const visitas = [...next[diaIndex].visitas];
    const atual = visitas[visitaIndex];
    const slugVisitaTecnica = tiposServico.find((t) => t.slug === 'visita_tecnica')?.slug;
    visitas[visitaIndex] = {
      ...atual,
      modo,
      ...(modo === 'avulso'
        ? {
            cliente_id: '',
            cliente_codigo: '',
            cliente_nome: '',
            tipo_servico: atual.tipo_servico || slugVisitaTecnica || '',
          }
        : { avulso: avulsoVazio() }),
    };
    next[diaIndex] = { ...next[diaIndex], visitas };
    setDias(next);
  };

  const updateVisitaAvulso = (diaIndex, visitaIndex, avulso) => {
    const next = [...dias];
    const visitas = [...next[diaIndex].visitas];
    visitas[visitaIndex] = { ...visitas[visitaIndex], avulso };
    next[diaIndex] = { ...next[diaIndex], visitas };
    setDias(next);
  };

  const totalVisitas = dias.reduce((acc, d) => acc + d.visitas.length, 0);
  const visitasProntas = dias.reduce(
    (acc, d) => acc + d.visitas.filter(visitaPronta).length,
    0
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tecnicoId) {
      toast.error('Selecione o técnico');
      return;
    }

    if (dias.length === 0) {
      toast.error('Adicione pelo menos um dia');
      return;
    }

    for (let i = 0; i < dias.length; i++) {
      const dia = dias[i];
      if (!dia.data) {
        toast.error(`Informe a data do Dia ${i + 1}`);
        return;
      }
      if (dia.visitas.length === 0) {
        toast.error(`Adicione pelo menos um cliente no Dia ${i + 1}`);
        return;
      }
      if (dia.visitas.some((v) => v.modo === 'avulso' ? !avulsoPreenchido(v.avulso) : !v.cliente_id)) {
        toast.error(`Informe o cliente ou o local avulso em todas as paradas do Dia ${i + 1}`);
        return;
      }
      if (dia.visitas.some((v) => !v.tipo_servico)) {
        toast.error(`Selecione o tipo de serviço em todas as paradas do Dia ${i + 1}`);
        return;
      }
    }

    const datas = dias.map((d) => d.data);
    if (new Set(datas).size !== datas.length) {
      toast.error('Cada dia deve ter uma data diferente');
      return;
    }

    setSaving(true);
    try {
      const tecnico = tecnicos.find((t) => t.id === tecnicoId);
      const tecnicoNome = tecnico?.name || tecnico?.nome || '';

      for (const dia of dias) {
        const visitas = [];
        for (const visita of dia.visitas) {
          if (visita.modo === 'avulso') {
            const criado = await cadastrarLocalAvulso(visita.avulso);
            visitas.push({
              cliente_id: criado.id,
              cliente_codigo: criado.codigo,
              cliente_nome: criado.nome,
              ordem: visita.ordem,
              horario_agendado: visita.horario_agendado,
              tipo_servico: visita.tipo_servico,
            });
          } else {
            visitas.push({
              cliente_id: visita.cliente_id,
              cliente_codigo: visita.cliente_codigo,
              cliente_nome: visita.cliente_nome,
              ordem: visita.ordem,
              horario_agendado: visita.horario_agendado,
              tipo_servico: visita.tipo_servico,
            });
          }
        }
        await api.post('/rotas', {
          data: dia.data,
          tecnico_id: tecnicoId,
          tecnico_nome: tecnicoNome,
          km_inicial: 0,
          km_final: 0,
          carro_id: '',
          carro_placa: '',
          visitas,
        });
      }

      toast.success(
        dias.length === 1
          ? 'Rota criada com sucesso!'
          : `${dias.length} rotas criadas com sucesso!`
      );
      navigate('/rotas');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar rota');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto pb-28 animate-fade-in" data-testid="nova-rota-page">
      <div className="mb-6">
        <Link
          to="/rotas"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-emerald-600 transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para rotas
        </Link>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0 shadow-sm">
            <Route className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Nova rota</h1>
            <p className="text-slate-600 mt-1">
              Defina o técnico e um ou mais dias. Cada dia vira uma rota separada.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/80">
            <h2 className="text-base font-semibold text-slate-900">1. Técnico</h2>
            <p className="text-sm text-slate-500 mt-0.5">Mesmo técnico para todos os dias</p>
          </div>
          <div className="p-6">
            <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-2">
              <User className="w-4 h-4 text-slate-400" /> Técnico
            </label>
            <select
              value={tecnicoId}
              onChange={(e) => setTecnicoId(e.target.value)}
              className={fieldClass}
              required
              data-testid="tecnico-select"
            >
              <option value="">Selecione o técnico...</option>
              {tecnicos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || t.nome}
                </option>
              ))}
            </select>
          </div>
        </section>

        {dias.map((dia, diaIndex) => {
          const clientesJaNoDia = dia.visitas.map((v) => v.cliente_id).filter(Boolean);
          const prontasDia = dia.visitas.filter(visitaPronta).length;

          return (
            <section
              key={diaIndex}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
              data-testid={`dia-section-${diaIndex}`}
            >
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/80 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">
                    Dia {diaIndex + 1}
                  </h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {dia.visitas.length === 0
                      ? 'Data e clientes deste dia'
                      : `${prontasDia} de ${dia.visitas.length} parada(s) prontas`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {dias.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDia(diaIndex)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Remover este dia"
                    >
                      <Trash2 className="w-4 h-4" /> Remover dia
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => addVisita(diaIndex)}
                    className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors shadow-sm"
                    data-testid={`add-visita-btn-${diaIndex}`}
                  >
                    <Plus className="w-4 h-4" /> Adicionar parada
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6 space-y-4">
                <div className="max-w-xs">
                  <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-2">
                    <Calendar className="w-4 h-4 text-slate-400" /> Data
                  </label>
                  <input
                    type="date"
                    value={dia.data}
                    onChange={(e) => updateDiaData(diaIndex, e.target.value)}
                    className={fieldClass}
                    required
                    data-testid={`data-input-${diaIndex}`}
                  />
                </div>

                {dia.visitas.length === 0 && (
                  <button
                    type="button"
                    onClick={() => addVisita(diaIndex)}
                    className="w-full rounded-2xl border-2 border-dashed border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 py-12 px-6 text-center transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-full bg-slate-100 group-hover:bg-emerald-100 flex items-center justify-center mx-auto mb-3 transition-colors">
                      <Plus className="w-6 h-6 text-slate-400 group-hover:text-emerald-600" />
                    </div>
                    <p className="text-slate-700 font-medium">Nenhuma parada ainda</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Toque para adicionar um cliente ou um local avulso
                    </p>
                  </button>
                )}

                {dia.visitas.map((visita, visitaIndex) => (
                  <div
                    key={visitaIndex}
                    className="relative rounded-2xl border border-slate-200 bg-slate-50/50 p-4 sm:p-5"
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-9 h-9 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center shrink-0 mt-0.5">
                        {visitaIndex + 1}
                      </div>

                      <div className="flex-1 min-w-0 space-y-4">
                        <div>
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                            <label className="block text-sm font-medium text-slate-700">
                              Destino
                            </label>
                            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5 text-xs font-medium">
                              <button
                                type="button"
                                onClick={() => setVisitaModo(diaIndex, visitaIndex, 'cliente')}
                                className={`px-2.5 py-1 rounded-md transition-colors ${
                                  visita.modo !== 'avulso'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                                }`}
                              >
                                Cliente cadastrado
                              </button>
                              <button
                                type="button"
                                onClick={() => setVisitaModo(diaIndex, visitaIndex, 'avulso')}
                                className={`px-2.5 py-1 rounded-md transition-colors inline-flex items-center gap-1 ${
                                  visita.modo === 'avulso'
                                    ? 'bg-white text-slate-900 shadow-sm'
                                    : 'text-slate-500 hover:text-slate-700'
                                }`}
                              >
                                <MapPin className="w-3 h-3" /> Local avulso
                              </button>
                            </div>
                          </div>
                          {visita.modo === 'avulso' ? (
                            <LocalAvulsoCampos
                              value={visita.avulso}
                              onChange={(avulso) => updateVisitaAvulso(diaIndex, visitaIndex, avulso)}
                              fieldClass={fieldClass}
                            />
                          ) : (
                            <ClienteSelector
                              value={visita.cliente_id || null}
                              onChange={(cliente) =>
                                updateVisitaCliente(diaIndex, visitaIndex, cliente)
                              }
                              excludeIds={clientesJaNoDia.filter((id) => id !== visita.cliente_id)}
                            />
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1.5">
                              Tipo de serviço
                            </label>
                            <select
                              value={visita.tipo_servico || ''}
                              onChange={(e) =>
                                updateVisita(diaIndex, visitaIndex, 'tipo_servico', e.target.value)
                              }
                              className={fieldClass}
                              required
                            >
                              <option value="">Selecione...</option>
                              {tiposServico.map((t) => (
                                <option key={t.id || t.slug} value={t.slug}>
                                  {t.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-1.5">
                              <Clock className="w-3.5 h-3.5 text-slate-400" />
                              Horário (opcional)
                            </label>
                            <input
                              type="time"
                              value={visita.horario_agendado || ''}
                              onChange={(e) =>
                                updateVisita(
                                  diaIndex,
                                  visitaIndex,
                                  'horario_agendado',
                                  e.target.value
                                )
                              }
                              className={fieldClass}
                            />
                          </div>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeVisita(diaIndex, visitaIndex)}
                        className="shrink-0 p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Remover desta rota"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}

                {dia.visitas.length > 0 && (
                  <button
                    type="button"
                    onClick={() => addVisita(diaIndex)}
                    className="w-full py-3 rounded-xl border border-dashed border-slate-300 text-slate-600 text-sm font-medium hover:border-emerald-500 hover:text-emerald-700 hover:bg-emerald-50/50 transition-colors inline-flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> Mais uma parada
                  </button>
                )}
              </div>
            </section>
          );
        })}

        <button
          type="button"
          onClick={addDia}
          className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-300 text-slate-600 font-medium hover:border-emerald-500 hover:text-emerald-700 hover:bg-emerald-50/40 transition-colors inline-flex items-center justify-center gap-2"
          data-testid="add-dia-btn"
        >
          <Plus className="w-5 h-5" /> Adicionar outro dia
        </button>

        <div className="fixed bottom-0 inset-x-0 z-20 border-t border-slate-200 bg-white/95 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col-reverse sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => navigate('/rotas')}
              className="sm:w-40 px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-rota-btn"
            >
              {saving ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {dias.length === 1
                    ? `Salvar rota${totalVisitas > 0 ? ` (${totalVisitas})` : ''}`
                    : `Salvar ${dias.length} rotas${visitasProntas > 0 ? ` (${visitasProntas} paradas)` : ''}`}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default NovaRota;
